import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './MonorailScales.css';
import { useProducts, type MonorailScalesSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const MonorailScales: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products: models, loading, error } = useProducts<MonorailScalesSpecs>('monorail-scales', editMode);
  const navigate = useNavigate();

  const applications = [
    {
      title: 'Rastros y Mataderos',
      description: 'Pesaje preciso de canales de res, cerdo y otros animales durante el procesamiento.',
    },
    {
      title: 'Plantas Procesadoras',
      description: 'Control de peso en líneas de procesamiento de productos cárnicos.',
    },
    {
      title: 'Inspección Sanitaria',
      description: 'Registro de pesos para cumplimiento de normativas sanitarias y de calidad.',
    },
    {
      title: 'Control de Inventario',
      description: 'Seguimiento y control de inventario de productos cárnicos.',
    },
  ];

  const advantages = [
    { title: 'Construcción Robusta', description: 'Estructura de acero galvanizado resistente a la corrosión y uso industrial.' },
    { title: 'Flexibilidad', description: 'Múltiples configuraciones de capacidad y longitud de riel.' },
    { title: 'Precisión', description: 'Pesaje exacto para control de calidad y cumplimiento normativo.' },
    { title: 'Durabilidad', description: 'Diseñadas para resistir ambientes industriales exigentes.' },
    { title: 'Instalación', description: 'Fácil integración en líneas de producción existentes.' },
    { title: 'Mantenimiento', description: 'Diseño que facilita limpieza y mantenimiento regular.' },
  ];

  const customOptions = [
    { title: 'Capacidades Especiales', description: 'Desarrollo de básculas con capacidades específicas según su aplicación.' },
    { title: 'Longitudes Personalizadas', description: 'Rieles con longitudes especiales para instalaciones particulares.' },
    { title: 'Materiales Especiales', description: 'Opciones de materiales especializados para ambientes específicos.' },
  ];

  return (
    <div className="mns-page">
      {error && <div className="mns-status-bar mns-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="mns-hero">
        <div className="container mns-hero-content">
          <nav className="mns-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="mns-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="mns-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Básculas de Monorriel</span>
          </nav>
          <h1 className="mns-hero-title">Básculas de Monorriel</h1>
          <p className="mns-hero-subtitle">
            Sistemas de pesaje suspendido especializados para la industria
            ganadera y el procesamiento cárnico.
          </p>
          {authType === 'internal' && (
            <button
              className={`mns-btn-edit-mode${editMode ? ' mns-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="mns-hero-image" aria-hidden="true">
          <img src="/images/monorail-scales.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="mns-overview">
        <div className="container">
          <div className="mns-overview-grid">
            <div className="mns-overview-text">
              <span className="mns-label">Descripción general</span>
              <h2>Pesaje en línea para la industria cárnica</h2>
              <p>
                Las básculas de monorriel están diseñadas principalmente para la industria ganadera,
                especialmente en instalaciones de procesamiento de carne. Son ideales para el pesaje
                de canales de animales, permitiendo un seguimiento preciso y registro de pesos en rastros
                con una construcción robusta de acero galvanizado y protección NEMA 4 en acero inoxidable.
              </p>
            </div>
            <div className="mns-overview-image">

            </div>
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="mns-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Modelos Disponibles</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/monorail-scales')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : models.map((model, index) => (
                <ProductCard
                  key={index}
                  reviewed={model.reviewed}
                  endOfLife={model.endOfLife}
                  productId={model.productId}
                  name={model.name}
                  badge={model.specs?.capacity ?? undefined}
                  sub={model.specs?.railLength ? `Riel: ${model.specs?.railLength ?? undefined}` : undefined}
                  description={model.specs?.description ?? undefined}
                  image={model.image}
                  onRevisar={editMode && model.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(model.specs?.features?.length ? [{ icon: '⚙️', label: 'Características', items: (model.specs?.features  ?? []).filter((x): x is string => x != null)}] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="mns-applications">
        <div className="container">
          <span className="mns-label mns-label--light">Industrias</span>
          <h2 className="mns-section-title mns-section-title--light">Aplicaciones</h2>
          <div className="mns-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="mns-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Advantages ───────────────────────────────────────── */}
      <section className="mns-advantages">
        <div className="container">
          <span className="mns-label">Beneficios</span>
          <h2 className="mns-section-title">Ventajas de Nuestras Básculas de Monorriel</h2>
          <div className="mns-advantages-grid">
            {advantages.map((adv, index) => (
              <div key={index} className="mns-advantage-card">
                <h3>{adv.title}</h3>
                <p>{adv.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Custom solutions ─────────────────────────────────── */}
      <section className="mns-custom">
        <div className="container">
          <div className="mns-custom-inner">
            <div className="mns-custom-header">
              <span className="mns-label">A la medida</span>
              <h2 className="mns-section-title">Soluciones Personalizadas</h2>
              <p>
                En ABC Básculas estamos abiertos a personalizar soluciones para
                satisfacer las necesidades específicas de su operación.
                Nuestro equipo de ingenieros puede desarrollar configuraciones
                especiales según sus requerimientos particulares.
              </p>
            </div>
            <div className="mns-custom-grid">
              {customOptions.map((opt, index) => (
                <div key={index} className="mns-custom-card">
                  <h3>{opt.title}</h3>
                  <p>{opt.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MonorailScales;
